import type { Action } from 'components/Layout/PaymentViewLayout';
import type { InvoiceStatus, User } from 'payments-api/model';
import { PaymentViewLayout } from 'components/Layout/PaymentViewLayout';
import React from 'react';

import { type CurrencyCode } from '@fragment/types/currencyCodes';
import { currencyPrecision as currencyPrecisionMap } from '@fragment/types/currencyCodes';
import { Dropdown } from '@fragment/ui/src/components/Dropdown/Dropdown';
import { CurrencyField } from '@fragment/ui/src/components/Payments/CurrencyField';
import {
  LayoutCell,
  LayoutRow,
  TextCell,
} from '@fragment/ui/src/components/Payments/LayoutRow';
import { StatusBadge } from '@fragment/ui/src/components/Payments/StatusBadge';

type Props = {
  onBack: () => void;
  title: string;
  primaryAction?: Action;
  secondaryAction?: Action;
  status: InvoiceStatus;
  totalAmount: {
    amount: number;
    currency: CurrencyCode;
  }[];
  issued: string;
  buyer?: string;
  parties?: User[];
  setBuyer?: (buyer: string) => void;
  setTitle?: (title: string) => void;
  children: React.ReactNode;
};

export const InvoiceLayout = ({
  onBack,
  title,
  primaryAction,
  secondaryAction,
  status,
  totalAmount,
  issued,
  buyer,
  parties,
  setBuyer,
  setTitle,
  children,
}: Props) => (
  <PaymentViewLayout
    title={title}
    onBack={onBack}
    primaryAction={primaryAction}
    secondaryAction={secondaryAction}
  >
    {/* start title section */}
    <LayoutRow height="large">
      <TextCell
        text={title}
        textSize="lg"
        align="left"
        border="none"
        onChange={setTitle ? (text) => setTitle(text) : undefined}
        fullWidth
        data-testid="invoice-title-input"
      />
      <LayoutCell border="left">
        <StatusBadge status={status as 'draft' | 'closed' | 'open'} />
      </LayoutCell>
    </LayoutRow>

    {/* start detail section */}

    {(buyer !== undefined || (setBuyer && parties)) && (
      <LayoutRow height="small" justify="start">
        <TextCell
          text="Buyer"
          textSize="md"
          align="left"
          border="right"
          secondary
        />
        {setBuyer && parties ? (
          <div className="flex h-full p-f1 w-full">
            <Dropdown
              options={parties.map((user) => ({
                value: user.externalId,
                label: user.externalId,
              }))}
              displayValue={buyer ?? ''}
              onChange={setBuyer}
              placeholder="Select"
              data-testid="buyer-party-dropdown"
              size="md"
              width="w-full"
              height="h-full"
            />
          </div>
        ) : (
          <TextCell
            text={buyer ?? ''}
            textSize="md"
            align="left"
            border="none"
            fullWidth
            data-testid="buyer-party-text"
          />
        )}
      </LayoutRow>
    )}
    <LayoutRow height="small" justify="start">
      <TextCell
        text="Issued"
        textSize="md"
        align="left"
        border="right"
        secondary
      />
      <TextCell
        text={new Date(issued).toDateString().split(' ').slice(1).join(' ')}
        textSize="md"
        align="left"
        border="none"
        useMonoText
      />
    </LayoutRow>
    <LayoutRow height="medium" justify="start">
      <TextCell
        text="Amount"
        textSize="md"
        align="left"
        border="right"
        secondary
      />
      <LayoutCell border="none" align="top" width="full">
        {totalAmount.map((amount) => (
          <CurrencyField
            key={amount.currency}
            amount={amount.amount}
            currencyPrecision={
              currencyPrecisionMap[amount.currency as CurrencyCode]
            }
            currencyCode={amount.currency as CurrencyCode}
            textSize="md"
            align="left"
          />
        ))}
      </LayoutCell>
    </LayoutRow>
    {children}
  </PaymentViewLayout>
);
